import { ReactElement } from 'react';
import { Trans, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { nsrTimePeriods } from './filters';
import { ComparisonText } from '.';

interface ChangeProps {
  difference: number;
  forTooltip?: boolean;
  timePeriod?: keyof typeof nsrTimePeriods;
}

export const PatientsTypeComparisonText = ({ difference, forTooltip, timePeriod }: ChangeProps) => {
  const { t } = useTranslation('analytics');

  let content: ReactElement | string = <></>;

  let currentPeriod = '';

  switch (timePeriod) {
    case 'lastMonth':
      currentPeriod = t('last month');
      break;

    case 'thisMonth':
      currentPeriod = t('this month');
      break;

    case 'thisQuarter':
      currentPeriod = t('this quarter');
      break;

    default:
      currentPeriod = '';
      break;
  }

  const transValues = {
    difference: formatters.appendPercent(Math.abs(difference)),
  };

  if (forTooltip) {
    if (difference > 0) {
      content = (
        <Trans t={t} values={transValues}>
          New patients show{' '}
          <strong style={{ color: theme.colors.critical50 }}>{{ difference: transValues.difference }}</strong> higher
          no-show rate compared to existing patients
        </Trans>
      );
    } else if (difference < 0) {
      content = (
        <Trans t={t} values={transValues}>
          New patients show <strong>{{ difference: transValues.difference }}</strong> lower no-show rate compared to
          existing patients
        </Trans>
      );
    } else {
      content = t('New patients show the same no-show rate as existing patients');
    }
    return content;
  }

  if (timePeriod === 'thisMonth' || timePeriod === 'thisQuarter') {
    if (difference > 0) {
      content = (
        <Trans t={t} values={transValues}>
          New patients have an average no-show rate <strong>{{ difference: transValues.difference }}</strong> higher
          than existing patients {{ currentPeriod }}
        </Trans>
      );
    } else if (difference < 0) {
      content = (
        <Trans t={t} values={transValues}>
          New patients have an average no-show rate <strong>{{ difference: transValues.difference }}</strong> lower than
          existing patients {{ currentPeriod }}
        </Trans>
      );
    } else {
      content = t('New patients have the same no-show rate as existing patients {{ currentPeriod }}', {
        currentPeriod,
      });
    }
  } else {
    if (difference > 0) {
      content = (
        <Trans t={t} values={transValues}>
          New patients had an average no-show rate <strong>{{ difference: transValues.difference }}</strong> higher than
          existing patients {{ currentPeriod }}
        </Trans>
      );
    } else if (difference < 0) {
      content = (
        <Trans t={t} values={transValues}>
          New patients had an average no-show rate <strong>{{ difference: transValues.difference }}</strong> lower than
          existing patients {{ currentPeriod }}
        </Trans>
      );
    } else {
      content = t('New patients had the same no-show rate as existing patients {{ currentPeriod }}', {
        currentPeriod,
      });
    }
  }

  return <ComparisonText content={content} />;
};
